import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowUpRight, Linkedin, Mail } from "lucide-react";
import { EASE } from "@/lib/motion";

const LINE_MASK = {
  hidden: { y: "110%" },
  show: (i = 0) => ({
    y: 0,
    transition: { delay: 0.35 + i * 0.09, duration: 1, ease: EASE },
  }),
};

const FADE_IN = { opacity: 0 };
const SHOWN = { opacity: 1 };
const FADE_UP_20 = { opacity: 0, y: 20 };
const FADE_UP_40 = { opacity: 0, y: 40 };
const RISEN = { opacity: 1, y: 0 };
const PORTRAIT_INITIAL = { opacity: 0, scale: 1.05 };
const PORTRAIT_SHOWN = { opacity: 1, scale: 1 };
const PORTRAIT_TRANSITION = { delay: 0.6, duration: 1.4, ease: EASE };

function MaskLine({ children, i = 0, className = "" }) {
  return (
    <span className={`mask-line ${className}`}>
      <motion.span
        variants={LINE_MASK}
        initial="hidden"
        animate="show"
        custom={i}
        className="block will-change-transform"
      >
        {children}
      </motion.span>
    </span>
  );
}

const HeroEyebrows = () => (
  <div className="col-span-12 flex items-center justify-between mb-8 md:mb-14">
    <motion.span
      initial={FADE_IN}
      animate={SHOWN}
      transition={{ delay: 0.15, duration: 1 }}
      className="eyebrow"
      data-testid="hero-location"
    >
      Dubai, United Arab Emirates
    </motion.span>
    <motion.span
      initial={FADE_IN}
      animate={SHOWN}
      transition={{ delay: 0.2, duration: 1 }}
      className="hidden md:inline eyebrow text-steel"
    >
      DBA (AI/ML) &middot; UT AUSTIN &middot; ACIM
    </motion.span>
  </div>
);

const HeroPortrait = ({ portraitY, portraitScale }) => (
  <div className="col-span-12 lg:col-span-4 relative order-first lg:order-none mb-4 lg:mb-0">
    <motion.div
      style={{ y: portraitY, scale: portraitScale }}
      initial={PORTRAIT_INITIAL}
      animate={PORTRAIT_SHOWN}
      transition={PORTRAIT_TRANSITION}
      className="relative aspect-[3/4] w-[55%] lg:w-full max-w-[360px] ml-auto border border-stroke overflow-hidden bg-matte"
      data-testid="hero-portrait"
    >
      <img
        src="/hero-portrait.webp"
        alt="Himanshu Oberoi — Performance Marketing Lead"
        width="720"
        height="960"
        fetchPriority="high"
        decoding="async"
        className="w-full h-full object-cover object-top"
        loading="eager"
      />
    </motion.div>

    <motion.div
      initial={FADE_UP_40}
      animate={RISEN}
      transition={{ delay: 1.1, duration: 1 }}
      className="hidden lg:block absolute -left-16 bottom-6 bg-matte border border-stroke px-5 py-4"
      data-testid="hero-floating-stat"
    >
      <div className="font-mono text-[10px] tracking-[0.24em] text-laser">MONTHLY SPEND</div>
      <div className="font-mono text-3xl text-ink mt-1">$5M</div>
    </motion.div>
  </div>
);

const HeroTagline = () => (
  <motion.div
    initial={FADE_UP_20}
    animate={RISEN}
    transition={{ delay: 1.15, duration: 1 }}
    className="col-span-12 lg:col-span-7 mt-12 md:mt-16"
  >
    <div className="hairline mb-8" />
    <p
      className="font-mono text-xs md:text-sm tracking-[0.28em] uppercase text-laser mb-6"
      data-testid="hero-title"
    >
      Performance Marketing Lead &middot; Founder, Digital Centaur
    </p>
    <p
      className="font-serif font-light text-3xl md:text-4xl lg:text-[3rem] leading-[1.05] text-ink max-w-3xl"
      data-testid="hero-tagline"
    >
      I engineer paid acquisition into <span className="text-laser">revenue</span> &mdash; $5M monthly spend, $300M+ revenue, 300–450% ROAS across 40+ countries.
    </p>
  </motion.div>
);

const scrollToContact = (e) => {
  e.preventDefault();
  document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
};

const HeroCtas = () => (
  <motion.div
    initial={FADE_UP_20}
    animate={RISEN}
    transition={{ delay: 1.35, duration: 0.9 }}
    className="col-span-12 lg:col-span-5 lg:justify-self-end mt-8 lg:mt-16"
  >
    <div className="flex flex-wrap items-center gap-3">
      <a
        href="https://www.linkedin.com/in/himanshukumaroberoi"
        target="_blank"
        rel="noopener noreferrer"
        data-testid="hero-cta-linkedin"
        className="btn-sweep btn-sweep-primary group inline-flex items-center gap-3 border border-laser text-ink px-6 py-4 font-mono text-xs tracking-[0.24em] uppercase"
      >
        <Linkedin size={16} strokeWidth={1.5} />
        Connect on LinkedIn
        <ArrowUpRight size={16} strokeWidth={1.5} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
      </a>
      <a
        href="#contact"
        onClick={scrollToContact}
        data-testid="hero-cta-email"
        className="btn-sweep inline-flex items-center gap-3 border border-stroke text-ink px-6 py-4 font-mono text-xs tracking-[0.24em] uppercase"
      >
        <Mail size={16} strokeWidth={1.5} />
        Send a Message
      </a>
    </div>
    <div className="mt-8 flex items-center gap-3 text-steel font-mono text-[11px] tracking-[0.2em] uppercase">
      <span className="w-6 h-px bg-stroke" />
      Fintech &middot; Trading &middot; Crypto &middot; eCommerce
    </div>
  </motion.div>
);

export default function Hero() {
  const wrap = useRef(null);
  const { scrollYProgress } = useScroll({ target: wrap, offset: ["start start", "end start"] });
  const portraitY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const portraitScale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section
      id="top"
      ref={wrap}
      className="relative min-h-[100vh] pt-28 md:pt-32 pb-24 overflow-hidden"
      data-testid="hero-section"
    >
      <div className="container-x relative z-10 grid grid-cols-12 gap-6 md:gap-8 items-start">
        <HeroEyebrows />

        <motion.div
          style={{ y: textY }}
          className="col-span-12 lg:col-span-8"
          data-testid="hero-name"
        >
          <h1 className="font-serif font-light text-ink leading-[0.88] tracking-tight text-[16vw] md:text-[13vw] lg:text-[11.5vw]">
            <MaskLine i={0}>HIMANSHU</MaskLine>
            <MaskLine i={1}>OBEROI.</MaskLine>
          </h1>
        </motion.div>

        <HeroPortrait portraitY={portraitY} portraitScale={portraitScale} />
        <HeroTagline />
        <HeroCtas />
      </div>
    </section>
  );
}
